import React, { useEffect, useState } from "react";
import { House, Search, Shirt, ShoppingBag, UserRound } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useCart } from "../../lib/cart";
import { useI18n } from "../../lib/i18n";
import { MOBILE_NAV } from "../../constants/testIds";

const COPY = {
  az: {
    label: "Mobil naviqasiya",
    home: "Ana səhifə",
    search: "Axtarış",
    wardrobe: "Qarderob",
    cart: "Səbət",
    profile: "Profil",
  },
  en: {
    label: "Mobile navigation",
    home: "Home",
    search: "Search",
    wardrobe: "Wardrobe",
    cart: "Cart",
    profile: "Profile",
  },
};

/* PDP-də alt xromun sahibi satınalma kapsuludur — iki üzən panel üst-üstə
   yığılıb ekranın dörddə birini yeyirdi. Geri yol header və breadcrumb-dadır. */
const HIDDEN_PATHS = ["/checkout", "/activate", "/reset-password", "/products"];

function MobileNavItem({ active, badge, icon: Icon, label, onClick, testId, to }) {
  /* İkon-only lüks dock: etiket vizual olaraq yoxdur (aria-label qalır),
     aktiv ikon krem "coin" üzərində oturur — CSS-dəki ::before çəkir. */
  return (
    <Link
      to={to}
      onClick={onClick}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={`adf-mobile-bottom-nav__item ${active ? "is-active" : ""}`}
      data-testid={testId}
    >
      <span className="adf-mobile-bottom-nav__icon">
        <Icon size={22} strokeWidth={active ? 1.7 : 1.45} aria-hidden="true" />
        {badge ? <span className="adf-mobile-bottom-nav__badge">{badge}</span> : null}
      </span>
    </Link>
  );
}

export default function MobileBottomNavigation() {
  const { locale } = useI18n();
  const { user } = useAuth() || { user: null };
  const { itemCount } = useCart() || { itemCount: 0 };
  const location = useLocation();
  // Header ilə eyni davranış: aşağı scroll-da bar gizlənir, yuxarı scroll-un
  // ilk hərəkətində qayıdır — kiçik ekran xrom üçün yox, məhsul üçün qalır.
  const [barHidden, setBarHidden] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastY && y > 140) setBarHidden(true);
      else if (y < lastY - 4) setBarHidden(false);
      lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const copy = COPY[locale] || COPY.az;
  const params = new URLSearchParams(location.search);
  const accountTab = params.get("tab") || "overview";
  const hidden = HIDDEN_PATHS.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

  if (hidden) return null;

  const wardrobeDestination = user
    ? "/account?tab=wardrobe"
    : `/auth/login?next=${encodeURIComponent("/account?tab=wardrobe")}`;
  const profileDestination = user
    ? "/account"
    : `/auth/login?next=${encodeURIComponent("/account")}`;

  const openSearch = (event) => {
    if (location.pathname !== "/shop") return;
    event.preventDefault();
    window.dispatchEvent(new CustomEvent("atelier:open-search"));
  };

  return (
    <>
      <div className="adf-mobile-bottom-nav-spacer" aria-hidden="true" />
      <nav
        className="adf-mobile-bottom-nav"
        aria-label={copy.label}
        data-hidden={barHidden ? "true" : "false"}
        data-testid={MOBILE_NAV.BAR}
      >
        <MobileNavItem
          to="/"
          icon={House}
          label={copy.home}
          active={location.pathname === "/"}
          testId={MOBILE_NAV.HOME}
        />
        <MobileNavItem
          to="/shop?search=1"
          icon={Search}
          label={copy.search}
          active={location.pathname === "/shop"}
          onClick={openSearch}
          testId={MOBILE_NAV.SEARCH}
        />
        <MobileNavItem
          to={wardrobeDestination}
          icon={Shirt}
          label={copy.wardrobe}
          active={location.pathname === "/account" && accountTab === "wardrobe"}
          testId={MOBILE_NAV.WARDROBE}
        />
        <MobileNavItem
          to="/cart"
          icon={ShoppingBag}
          label={copy.cart}
          active={location.pathname === "/cart"}
          badge={itemCount > 0 ? (itemCount > 9 ? "9+" : itemCount) : null}
          testId={MOBILE_NAV.CART}
        />
        <MobileNavItem
          to={profileDestination}
          icon={UserRound}
          label={copy.profile}
          active={location.pathname === "/account" && accountTab !== "wardrobe"}
          testId={MOBILE_NAV.PROFILE}
        />
      </nav>
    </>
  );
}
